import clsx from "clsx";
import firebase from "firebase/app";
import parse from "html-react-parser";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { FC, useState, ReactNode } from "react";
import { CSSTransition } from "react-transition-group";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { DialogEnum } from "@/consts/DialogEnum";
import { HeartIcon } from "@/features/common/components/icons/HeartIcon";
import { OptionIcon } from "@/features/common/components/icons/OptionIcon";
import { Container } from "@/features/common/components/layout/Container";
import type { StandardPopOverOption } from "@/features/common/components/layout/StandardPopoverButton";
import { useUIContext } from "@/features/common/hooks/UIContext/useUIContext";
import { useOnClickEth721CancelList } from "@/features/nft/hooks/useOnClickEth721CancelList";
import { useOnClickEth721UpdatePrice } from "@/features/nft/hooks/useOnClickEth721UpdatePrice";
import { Eth721Asset } from "@/features/nft/types";
import TextPlaceholder from "@/features/placeholder/TextPlaceholder";
import { ImageCardLikeButton } from "@/features/post/components/postCard/components/ImageCardLikeButton";
import { ImageCardRemoveLikeButton } from "@/features/post/components/postCard/components/ImageCardRemoveLikeButton";
import { useEditPostButton } from "@/features/post/hooks/useEditPostButton";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  PostDetailFragment,
  PostPagePostFragment,
  UploadWithIntrinsicSizeFragment,
  usePostInfoWithLoggedInUserQuery,
} from "@/generated/generated-hooks";

import { NFTDetail } from "./NFTDetail";
import { NFTPostContentInfoCard } from "./NFTPostContentInfoCard";

const StandardPopoverButton = dynamic(
  () => import("@/features/common/components/layout/StandardPopoverButton"),
  { ssr: false },
);

enum NFTPostContentMenuList {
  "info" = "INFO",
  "history" = "HISTORY",
  "bids" = "BIDS",
}

interface CollectiblePostContentProps {
  nftHistory: ReactNode;
  actionButton: ReactNode;
  post?: PostDetailFragment &
    PostPagePostFragment & { images?: UploadWithIntrinsicSizeFragment[] };
  asset?: Partial<Eth721Asset>;
  contractAddress: string;
  isNftOwner: boolean;
  isNftCreator?: boolean;
  isBid?: boolean;
}

/**
 * @purpose render a NFT post
 * @returns conditionally rendered %PostContent Component
 */
export const CollectiblePostContent: FC<CollectiblePostContentProps> = (
  props,
) => {
  const {
    nftHistory,
    isBid = false,
    actionButton,
    asset,
    post,
    isNftOwner,
    isNftCreator,
    contractAddress,
  } = props;
  const { t } = useTranslation("common");
  const { user } = useUserContext();
  const { setReportPostDialog, openDialog } = useUIContext();

  const onClickCancelList = useOnClickEth721CancelList({
    tokenId: post.tokenId,
    postId: post?._id,
  });
  const onClickUpdatePrice = useOnClickEth721UpdatePrice({
    tokenId: post.tokenId,
    postId: post?._id,
  });
  const { onClickEditPostButton, editPostDisabled, editPostLabel } =
    useEditPostButton({
      postDetail: post,
      logEvent: AnalyticsEventEnum.NftPostContentActionMenuEdit,
    });

  // remaining post data: images etc
  const { data: postInfo } = usePostInfoWithLoggedInUserQuery({
    variables: { slug: post.slug },
    context: { batch: true },
  });

  const hasAlpha = postInfo?.post?.images[0].hasAlpha;

  /**
   * Toggle between menus through the PostContentNavbar
   */
  const [activeMenu, setActiveMenu] = useState<NFTPostContentMenuList>(
    NFTPostContentMenuList.info,
  );

  const onClickReport = () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.NftPostContentActionMenuReportPost, {
        postId: post?._id,
      });
    if (!user?._id) return openDialog(DialogEnum.Login);
    const { _id, title, writer, slug } = post;
    setReportPostDialog({
      open: true,
      post: { _id, title, writerId: writer._id, slug },
    });
  };

  const getPopoverOptions = (): StandardPopOverOption[] => {
    if (!isNftOwner)
      return [
        {
          content: post.hasCurrentUserReport
            ? t("common:post.report.reportedMenuButtonLabel")
            : t("common:post.report.menuButtonLabel"),
          onClick: onClickReport,
          as: "button" as const,
          disabled: post.hasCurrentUserReport,
        },
      ];

    const ownerOptions = [];

    if (isNftCreator)
      ownerOptions.push({
        disabled: editPostDisabled,
        content: editPostLabel,
        onClick: onClickEditPostButton,
        as: "button" as const,
      });

    if (asset?.listStatus)
      ownerOptions.push(
        {
          content: t("common:nft.updatePrice"),
          onClick: onClickUpdatePrice,
          as: "button" as const,
        },
        {
          content: t("common:nft.cancelList"),
          onClick: onClickCancelList,
          as: "button" as const,
        },
      );

    return ownerOptions;
  };

  const popoverOptions = getPopoverOptions();

  return (
    <div className="text-base mx-auto w-full">
      {/* Image Display */}
      <div className="p-4">
        <div className="max-w-lg mx-auto overflow-hidden rounded-md">
          <div
            className={clsx(
              "aspect-w-8 aspect-h-8",
              hasAlpha ? "bg-checkerboard-pattern" : "bg-black",
            )}
          >
            <Image
              src={post.images[0].url}
              alt={post.title}
              layout="fill"
              objectFit="contain"
              priority={true}
            />
          </div>
        </div>
      </div>

      <Container>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-full md:col-span-8">
            <div className="w-full flex justify-between">
              <div className=" text-lg">{post.title}</div>
              {/* popover setting button */}

              <div className="text-gray-300 flex space-x-2 items-center">
                {asset && popoverOptions.length > 0 && (
                  /* TODO: can replaced with StandardPopoverButton */
                  <StandardPopoverButton
                    icon={
                      <div className="flex items-center border-2 border-gray-300 p-0.5 rounded-full text-opacity-75 hover:text-opacity-100 cursor-pointer">
                        <OptionIcon />
                      </div>
                    }
                    options={popoverOptions}
                  />
                )}
                {postInfo && postInfo.post?.hasCurrentUserLike ? (
                  <ImageCardRemoveLikeButton
                    postId={post._id}
                    logEvent={AnalyticsEventEnum.NftPostContentUnlike}
                  >
                    <div className="border-2 border-primary p-0.5 rounded-full bg-opacity-75 hover:bg-opacity-100 cursor-pointer">
                      <HeartIcon className="text-primary" />
                    </div>
                  </ImageCardRemoveLikeButton>
                ) : (
                  <ImageCardLikeButton
                    postId={post._id}
                    logEvent={AnalyticsEventEnum.NftPostContentLike}
                  >
                    <div className="border-2 border-gray-300 p-0.5 rounded-full bg-opacity-75 hover:bg-opacity-100 cursor-pointer">
                      <HeartIcon />
                    </div>
                  </ImageCardLikeButton>
                )}
              </div>
            </div>
            <div className="text-gray-900 mt-1 text-base ">
              {parse(post.content?.serialized ?? "")}
            </div>
          </div>
          <div className="relative col-span-full md:col-span-4">
            <div className="md:absolute w-full">
              <NFTPostContentInfoCard
                tokenId={post.tokenId}
                ownerAddress={asset?.owner}
                isListed={asset?.listStatus}
                listedPrice={asset?.price}
                loading={!asset}
                isNftOwner={isNftOwner}
                actionArea={
                  !!asset ? (
                    actionButton
                  ) : (
                    <div className="w-full md:max-w-350">
                      <TextPlaceholder size="3xl" />
                    </div>
                  )
                }
              />
            </div>
          </div>
        </div>
      </Container>

      <div className="mt-auto">
        {/* this is the PostContentNavbar */}
        <Container>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-full md:col-span-8 flex justify-start overflow-x-auto">
              <div
                className={`${
                  activeMenu === NFTPostContentMenuList.info
                    ? "border-primary text-primary cursor-default"
                    : "border-transparent text-gray-500 cursor-pointer"
                } inline-flex items-center px-7 py-2 border-b-4 font-medium text-sm`}
                onClick={() => {
                  firebase
                    .analytics()
                    .logEvent(AnalyticsEventEnum.NftPostContentNavInfo, {
                      postId: post?._id,
                    });
                  setActiveMenu(NFTPostContentMenuList.info);
                }}
              >
                {t("post:nft.tabs.detail")}
              </div>
              <div
                className={`${
                  activeMenu === NFTPostContentMenuList.history
                    ? "border-primary text-primary cursor-default"
                    : "border-transparent text-gray-500 cursor-pointer"
                } inline-flex items-center px-7 py-2 border-b-4 font-medium text-sm`}
                onClick={() => {
                  firebase
                    .analytics()
                    .logEvent(AnalyticsEventEnum.NftPostContentNavHistory, {
                      postId: post?._id,
                    });
                  setActiveMenu(NFTPostContentMenuList.history);
                }}
              >
                {t("post:nft.tabs.history")}
              </div>
              {isBid && (
                <div
                  className={`${
                    activeMenu === NFTPostContentMenuList.bids
                      ? "border-primary text-primary cursor-default"
                      : "border-transparent text-gray-500 cursor-pointer"
                  } inline-flex items-center px-7 py-2 border-b-4 font-medium text-sm`}
                  onClick={() => setActiveMenu(NFTPostContentMenuList.bids)}
                >
                  {NFTPostContentMenuList.bids}
                </div>
              )}
            </div>
          </div>
        </Container>

        {/* this is the PostContentMenuArea */}
        <div className="bg-gray-100 min-h-1/2">
          <Container>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-full md:col-span-8 py-12">
                {/* Info Menu */}
                <CSSTransition
                  classNames="position-content-menu"
                  in={activeMenu === NFTPostContentMenuList.info}
                  unmountOnExit
                  timeout={0}
                  //   onEnter={calcHeight}
                >
                  <NFTDetail
                    contractAddress={contractAddress}
                    post={post}
                    loading={!asset}
                    tokenId={post.tokenId}
                  />
                </CSSTransition>

                {/* History Menu */}
                <CSSTransition
                  classNames="position-content-menu"
                  in={activeMenu === NFTPostContentMenuList.history}
                  unmountOnExit
                  timeout={0}
                >
                  {nftHistory}
                </CSSTransition>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </div>
  );
};
